import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { TEAM } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialsOf = (name) =>
  (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export const Team = () => {
  const [team, setTeam] = useState(
    // Fallback imediato do content.js: evita flash de vazio enquanto o API responde
    TEAM.map((t) => ({ name: t.name, role: t.role, photo_url: t.image, bio: t.bio || "" }))
  );

  useEffect(() => {
    axios
      .get(`${API}/team`)
      .then((res) => {
        const list = res.data?.team || [];
        if (list.length) setTeam(list);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="equipa" data-testid="team-section" className="bg-[#FAFAFA] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="label-editorial text-clay mb-6">A nossa equipa</p>
        <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-none mb-16">
          Dedicados e <span className="italic text-clay">profissionais</span> para si
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl">
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              data-testid={`team-member-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className={i === 1 ? "md:mt-16" : ""}
            >
              <div className="img-hover-zoom h-96">
                <img
                  src={member.photo_url}
                  alt={`${member.name}, ${member.role} na INN.PACT Clínica de Imagem em Leiria`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <h3 className="font-display text-2xl md:text-3xl mt-6 tracking-tight">{member.name}</h3>
              {member.role && <p className="label-editorial text-black/45 mt-2">{member.role}</p>}
              {member.bio && (
                <p className="mt-4 text-sm text-black/60 font-light leading-relaxed max-w-md">
                  {member.bio}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then((res) => setTestimonials(res.data.testimonials || []))
      .catch(() => setTestimonials([]));
  }, []);

  if (testimonials.length === 0) return null;

  return (
    <section id="testemunhos" data-testid="testimonials-section" className="bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="label-editorial text-clay mb-6">Satisfação do cliente</p>
        <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-none mb-16">
          O que dizem <span className="italic text-clay">de nós</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/5 border border-black/5">
          {testimonials.map((t, i) => (
            <motion.blockquote
              key={t.id || t.name}
              data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="bg-white p-10 flex flex-col"
            >
              <span className="font-display text-6xl leading-none text-clay/30">“</span>
              <p className="font-display text-xl md:text-2xl leading-snug tracking-tight mt-2">
                {t.quote}
              </p>
              <footer className="mt-auto pt-8 flex items-center gap-4">
                {t.photo ? (
                  <img
                    src={t.photo}
                    alt={`Foto de ${t.name}, cliente da INN.PACT`}
                    className="w-12 h-12 rounded-full object-cover border border-black/10"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-black/5 border border-black/10 flex items-center justify-center label-editorial text-black/40 text-xs">
                    {initialsOf(t.name)}
                  </div>
                )}
                <div>
                  <p className="label-editorial">{t.name}</p>
                  <p className="text-xs text-black/40 mt-1 font-light">{t.role}</p>
                </div>
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </div>
    </section>
  );
};
